import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setProductData } from '../store/productSlice';
import { setAdminData } from '../store/adminSlice';
import { setCalendarData } from '../store/calendarSlice';
import { addTransaction, setTransactions } from '../store/salesSlice';
import { setBonusPrizeData } from '../store/bonusPrizeSlice';
import { generateBundle } from '../utils/cardGenerator';
import { PW_Products_API, PW_Stores_API, PW_Calendar_API, PW_Transactions_List_API, PW_Transaction_Save_API, PW_BonusPrize_API } from '../Server/ApiV2';
import Spinner from './Spinner';

export default function Coupon() {
  const dispatch = useDispatch();
  const { products, productTypes, productTypeLinks } = useSelector(s => s.product);
  const { stores } = useSelector(s => s.admin);
  const { schedules } = useSelector(s => s.calendar);
  const { transactions } = useSelector(s => s.sales);
  const { bonusSchedules } = useSelector(s => s.bonusPrize);
  const { user } = useSelector(s => s.user);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [prodRes, storeRes, calRes, txnRes, bonusRes] = await Promise.all([
        PW_Products_API(), PW_Stores_API(), PW_Calendar_API(), PW_Transactions_List_API(), PW_BonusPrize_API(),
      ]);
      if (cancelled) return;
      if (prodRes?.data?.success) dispatch(setProductData(prodRes.data.data));
      if (storeRes?.data?.success) dispatch(setAdminData({ stores: storeRes.data.data }));
      if (calRes?.data?.success) dispatch(setCalendarData(calRes.data.data));
      if (txnRes?.data?.success) dispatch(setTransactions(txnRes.data.data));
      if (bonusRes?.data?.success) dispatch(setBonusPrizeData(bonusRes.data.data));
      setPageLoading(false);
    })();
    return () => { cancelled = true; };
  }, [dispatch]);

  const activeStores = stores.filter(s => s.status === 'Active');
  const [storeId, setStoreId] = useState(activeStores[0]?.id || '');
  const [productId, setProductId] = useState(products[0]?.id || '');
  const [cards, setCards] = useState(null);
  const [pickedIdx, setPickedIdx] = useState(null);
  const [winCardIdx, setWinCardIdx] = useState(-1);
  const [revealed, setRevealed] = useState(false);

  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const todayM = today.getMonth() + 1;
  const todayY = today.getFullYear();
  const todayDow = today.getDay();

  const getSchedule = (sid, pid) => {
    const a = schedules.filter(s => s.status === 'Active' && s.storeIds.includes(sid)
      && (!s.productIds?.length || s.productIds.includes(pid)));
    return a.find(s => s.dateMode === 'day' && s.startDate === todayStr)
      || a.find(s => s.dateMode === 'weekday' && (s.weekdays || []).includes(todayDow))
      || a.find(s => s.dateMode === 'range' && todayStr >= s.startDate && todayStr <= s.endDate)
      || a.find(s => s.dateMode === 'month' && s.month === todayM && s.year === todayY)
      || null;
  };

  const schedule = getSchedule(storeId, productId);
  const allTypeOrder = schedule?.productTypeOrder || [];
  const linkedTypeIds = productTypeLinks.filter(l => l.linked && l.productId === productId).map(l => l.typeId);
  const typeOrder = allTypeOrder.filter(tid => linkedTypeIds.includes(tid));
  const product = products.find(p => p.id === productId);
  const batchSize = product?.cardsPerBundle || 100;

  const { activeTypeId, currentCycle } = useMemo(() => {
    if (!typeOrder.length || !product) return { activeTypeId: null, currentCycle: 1 };
    const todayTxns = transactions.filter(t =>
      t.storeId === storeId && t.productId === productId && t.timestamp?.startsWith(todayStr)
    );
    const total = todayTxns.length;
    const cycleSize = typeOrder.length * batchSize;
    const posInCycle = total % cycleSize;
    const idx = Math.floor(posInCycle / batchSize);
    return {
      activeTypeId: typeOrder[idx] || typeOrder[0],
      currentCycle: Math.floor(total / cycleSize) + 1,
    };
  }, [typeOrder, transactions, storeId, productId, todayStr, product, batchSize]);

  const link = activeTypeId
    ? productTypeLinks.find(l => l.productId === productId && l.typeId === activeTypeId && l.linked)
    : null;

  const [generating, setGenerating] = useState(false);
  const [genProduct, setGenProduct] = useState(null);

  const handleGenerate = async () => {
    if (!product || !link || generating) return;
    setGenerating(true);

    try {
      const freshRes = await PW_Products_API();
      let freshProduct = product;
      let freshLink = link;
      let freshAllLinks = productTypeLinks.filter(l => l.linked && l.productId === productId);

      if (freshRes?.data?.success) {
        dispatch(setProductData(freshRes.data.data));
        const { products: dbProducts, productTypeLinks: dbLinks } = freshRes.data.data;
        freshProduct = dbProducts.find(p => p.id === productId) || product;
        freshLink = dbLinks.find(l => l.productId === productId && l.typeId === activeTypeId && l.linked) || link;
        freshAllLinks = dbLinks.filter(l => l.linked && l.productId === productId);
      }

      setGenProduct(freshProduct);
      const freshBatchSize = freshProduct.cardsPerBundle || 100;
      const prodDenoms = [...new Set(freshAllLinks.flatMap(l =>
        Object.keys(l.winnerConfig || {}).map(Number).filter(d => d > 0)
      ))];
      const activeTypeTxns = transactions.filter(t =>
        t.storeId === storeId && t.productId === productId && t.typeId === activeTypeId && t.timestamp?.startsWith(todayStr)
      );
      const posInBatch = activeTypeTxns.length % freshBatchSize;

      const winnerSlots = [];
      for (const [d, c] of Object.entries(freshLink.winnerConfig)) {
        if (c <= 0) continue;
        for (let i = 0; i < c; i++) winnerSlots.push(Number(d));
      }
      while (winnerSlots.length < freshBatchSize) winnerSlots.push(null);

      const cycleNum = Math.floor(activeTypeTxns.length / freshBatchSize) + 1;
      const seedStr = `${storeId}-${productId}-${activeTypeId}-${todayStr}-${cycleNum}`;
      let hash = 0;
      for (let i = 0; i < seedStr.length; i++) hash = ((hash << 5) - hash) + seedStr.charCodeAt(i) | 0;
      let s = Math.abs(hash);
      const schedule = [...winnerSlots];
      for (let i = schedule.length - 1; i > 0; i--) {
        s = (s * 1103515245 + 12345) & 0x7fffffff;
        const j = s % (i + 1);
        [schedule[i], schedule[j]] = [schedule[j], schedule[i]];
      }
      if (schedule.length > freshBatchSize) schedule.length = freshBatchSize;

      const outcome = schedule[posInBatch];

      let isBonusWinner = false;
      let bonusDenom = null;
      let bonusScheduleHit = null;
      {
        const activeBonuses = getActiveBonuses();
        for (const ab of activeBonuses) {
          const bonusSlots = [true];
          while (bonusSlots.length < freshBatchSize) bonusSlots.push(false);
          const bSeedStr = `bonus-${ab.id}-${storeId}-${productId}-${activeTypeId}-${todayStr}-${cycleNum}`;
          let bh = 0;
          for (let i = 0; i < bSeedStr.length; i++) bh = ((bh << 5) - bh) + bSeedStr.charCodeAt(i) | 0;
          let bs = Math.abs(bh);
          for (let i = bonusSlots.length - 1; i > 0; i--) {
            bs = (bs * 1103515245 + 12345) & 0x7fffffff;
            const j = bs % (i + 1);
            [bonusSlots[i], bonusSlots[j]] = [bonusSlots[j], bonusSlots[i]];
          }
          if (bonusSlots.length > freshBatchSize) bonusSlots.length = freshBatchSize;
          if (bonusSlots[posInBatch]) {
            const denomKey = Object.keys(ab.denominations || {})[0];
            if (denomKey) {
              bonusDenom = Number(denomKey);
              isBonusWinner = true;
              bonusScheduleHit = ab;
              break;
            }
          }
        }
      }

      const finalOutcome = isBonusWinner ? bonusDenom : outcome;
      const wc = finalOutcome !== null ? { [finalOutcome]: 1 } : {};
      const bundle = generateBundle({ productWinCondition: freshProduct.matchingMethod || freshProduct.winCondition, winnerConfig: wc, cardCount: 10, denominations: prodDenoms });
      const winIdx = finalOutcome !== null ? bundle.findIndex(c => c.isWinner && c.winAmount === finalOutcome) : -1;
      if (isBonusWinner && winIdx >= 0) bundle[winIdx].isBonusWinner = true;

      setCards(bundle);
      setWinCardIdx(winIdx);
      setPickedIdx(null);
      setRevealed(false);
    } finally {
      setGenerating(false);
    }
  };

  const handlePick = (idx) => {
    if (pickedIdx !== null) return;
    let bundle = cards;
    if (winCardIdx >= 0 && winCardIdx !== idx) {
      bundle = [...cards];
      [bundle[idx], bundle[winCardIdx]] = [bundle[winCardIdx], bundle[idx]];
      setCards(bundle);
    }
    setPickedIdx(idx);
    recordSelection(bundle[idx], idx);
  };

  const getActiveBonuses = () => {
    if (!storeId || !productId) return [];
    const now = new Date();
    const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    return bonusSchedules.filter(bs => {
      const bsDate = bs.scheduleDate ? bs.scheduleDate.split('T')[0] : '';
      return bsDate === todayStr && bs.productId === productId && bs.status === 'Active'
        && (bs.storeIds || []).includes(storeId)
        && hhmm >= bs.startTime && hhmm <= bs.endTime;
    });
  };

  // Records the selected card (saves the transaction) the moment one of the 10
  // is picked — selection is persisted even if the operator never opens it.
  const recordSelection = (card, idx) => {
    const store = stores.find(s => s.id === storeId);
    const activeProduct = genProduct || product;
    const factor = activeProduct?.priceAmount || 1;
    const basePrize = (card.winAmount || 0) * factor;
    const activeBonuses = getActiveBonuses();
    const matchedBonus = card.isBonusWinner ? activeBonuses.find(ab => {
      const denomKey = Object.keys(ab.denominations || {})[0];
      return denomKey && Number(denomKey) === card.winAmount;
    }) || activeBonuses[0] : null;
    const txn = {
      id: `TXN-${Date.now()}`,
      selectionNo: idx + 1,
      storeId,
      storeName: store?.name || '',
      productId,
      productName: activeProduct?.name || '',
      typeId: activeTypeId,
      cardGrid: card.grid,
      isWinner: card.isWinner,
      winDenomination: card.winningNumber,
      winningNumbers: card.winningNumbers || (card.isWinner ? [card.winningNumber] : []),
      winPairs: card.winPairs || null,
      prize: basePrize,
      priceAmount: factor,
      bonusAmount: 0,
      bonusScheduleId: matchedBonus?.id || null,
      userId: user?.userId || '',
      userName: user?.userName || '',
      timestamp: new Date().toISOString(),
    };
    dispatch(addTransaction(txn));
    PW_Transaction_Save_API(txn).catch(err => console.error('[Coupon] Save failed:', err));
  };

  const handleReveal = () => {
    if (pickedIdx === null || revealed) return;
    setRevealed(true);
  };

  const handleNewRound = () => {
    setCards(null);
    setPickedIdx(null);
    setRevealed(false);
    setGenProduct(null);
  };

  const pickedCard = pickedIdx !== null ? cards[pickedIdx] : null;
  const todayTotal = transactions.filter(t => t.storeId === storeId && t.productId === productId && t.timestamp?.startsWith(todayStr)).length;
  const posInType = todayTotal % (typeOrder.length * batchSize) % batchSize;
  const remaining = batchSize - posInType;

  if (pageLoading) return <div className="page-loading"><Spinner /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Coupon Sales</h2>
          <div className="page-header-sub">Generate instance rewards for customers</div>
        </div>
      </div>

      <div className="card mb-24">
        <div className="card-title gold">Select Store & Product</div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', marginBottom: 16 }}>
            <div style={{ flex: 1, maxWidth: 220 }}>
              <div className="text-sm text-muted mb-8">Store</div>
              <select className="form-select" value={storeId}
                onChange={e => { setStoreId(e.target.value); handleNewRound(); }}>
                {activeStores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, maxWidth: 220 }}>
              <div className="text-sm text-muted mb-8">Product</div>
              <select className="form-select" value={productId}
                onChange={e => { setProductId(e.target.value); handleNewRound(); }}>
                {products.filter(p => p.status === 'Active').map(p => (
                  <option key={p.id} value={p.id}>{p.name} (RSP {p.rsp} · Price {p.priceAmount || 1})</option>
                ))}
              </select>
            </div>
          </div>

          {schedule ? (
            <div>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                <div>
                  <span className="text-sm text-muted">Schedule: </span>
                  {typeOrder.map((tid, i) => (
                    <React.Fragment key={tid}>
                      {i > 0 && <span className="cal-arrow">→</span>}
                      <span className={`chip ${tid === activeTypeId ? 'chip-green' : 'chip-purple'}`} style={{ fontSize: 11 }}>{tid}</span>
                    </React.Fragment>
                  ))}
                  <span className="text-sm text-muted" style={{ marginLeft: 12 }}>Cycle {currentCycle}</span>
                  {(() => {
                    const abs = getActiveBonuses();
                    return abs.length > 0 ? (
                      <span className="chip chip-gold" style={{ marginLeft: 8, fontSize: 10 }}>
                        +{abs.length} Bonus {abs.length > 1 ? 'Schedules' : 'Schedule'}
                      </span>
                    ) : null;
                  })()}
                </div>
                {link ? (
                  <button className="btn btn-success" onClick={handleGenerate} disabled={!!cards || generating}>
                    {generating ? 'Loading...' : 'Generate 10 Coupons'}
                  </button>
                ) : (
                  <span className="text-sm" style={{ color: '#e74c3c' }}>
                    Type {activeTypeId} not linked to {product?.name}
                  </span>
                )}
              </div>
              <div className="text-sm text-muted">
                Active: <strong style={{ color: '#00895e' }}>{activeTypeId}</strong>
                {' '} — {remaining} of {batchSize} remaining
                {typeOrder.length > 1 && (
                  <span> — then {typeOrder.filter(t => t !== activeTypeId).join(' → ')}</span>
                )}
              </div>
            </div>
          ) : (
            <div className="text-sm" style={{ color: '#e74c3c', padding: '8px 0' }}>
              No schedule configured for this store today. Set up in Calendar Settings.
            </div>
          )}
        </div>
      </div>

      {cards && (
        <div className="card mb-24">
          <div className={`card-title ${revealed && pickedCard?.isWinner ? (pickedCard.isBonusWinner ? 'gold' : 'green') : 'purple'}`}>
            {pickedIdx === null ? 'Pick Your Lucky Card!' : revealed ? (pickedCard?.isWinner ? (pickedCard.isBonusWinner ? 'Bonus Winner!' : 'Winner!') : 'Better luck next time') : 'Scratch to Reveal'}
          </div>
          <div className="card-body">
            <div className="scratch-grid">
              {cards.map((card, idx) => {
                const isPicked = pickedIdx === idx;
                const isOther = pickedIdx !== null && !isPicked;
                return (
                  <div key={idx}
                    className={`scratch-card${isPicked ? ' picked' : ''}${isOther ? ' dimmed' : ''}${isPicked && revealed && card.isWinner ? ' winner-glow' : ''}`}
                    onClick={() => handlePick(idx)}>
                    <div className="scratch-card-num">{idx + 1}</div>
                    <div className="scratch-card-star">★</div>
                  </div>
                );
              })}
            </div>

            {pickedIdx !== null && (
              <div className="scratch-reveal">
                <div>
                  <div className="scratch-grid-4x4">
                    {pickedCard.grid.flat().map((val, i) => (
                      <div key={i} className={`scratch-cell${revealed ? ' open' : ''}${revealed && pickedCard.isWinner && (pickedCard.winningNumbers ? pickedCard.winningNumbers.includes(val) : val === pickedCard.winningNumber) ? ' win-cell' : ''}`}>
                        {revealed ? val.toLocaleString() : '?'}
                      </div>
                    ))}
                  </div>
                  {!revealed && (
                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                      <button className="btn btn-success" onClick={handleReveal}>Open Card</button>
                    </div>
                  )}
                  {revealed && (
                    <div style={{ textAlign: 'center', marginTop: 12 }}>
                      {pickedCard.isWinner ? (() => {
                        const f = (genProduct || product)?.priceAmount || 1;
                        const base = pickedCard.winAmount * f;
                        return (
                          <div className="scratch-prize">
                            Prize: {pickedCard.winPairs && pickedCard.winPairs.length > 1
                              ? `(${pickedCard.winPairs.map(p => p.toLocaleString()).join(' + ')})${f > 1 ? ` × ${f}` : ''} = ${base.toLocaleString()}`
                              : `${pickedCard.winAmount.toLocaleString()}${f > 1 ? ` × ${f} = ${base.toLocaleString()}` : ''}`}
                          </div>
                        );
                      })() : (
                        <div className="text-muted fw-600">No prize this time</div>
                      )}
                      <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={handleNewRound}>New Round</button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
